package ai.jessie.web.work

import ai.jessie.shared.work.SaveWorkRequest
import ai.jessie.shared.work.WorkResponse
import ai.jessie.shared.work.WorkSelectionsResponse

// Editable text as entered. The server validates; the client only checks what it cannot send.
data class WorkDraft(
    val workDate: String = "",
    val durationMinutes: String = "",
    val memberId: String = "",
    val projectId: String = "",
    val positionId: String = "",
    val description: String = "",
)

// UC-009 BR-050: the calendar's selected date and, from a week-view period, its duration.
data class WorkPreset(val workDate: String, val durationMinutes: Int? = null)

// Step 4: today in the organisation's time zone and a single permitted member are preselected.
internal fun WorkSelectionsResponse.newDraft(preset: WorkPreset? = null) =
    WorkDraft(
        workDate = preset?.workDate ?: currentDate,
        durationMinutes = preset?.durationMinutes?.toString().orEmpty(),
        memberId = members.singleOrNull()?.id.orEmpty(),
    )

internal fun WorkResponse.draft(selections: WorkSelectionsResponse) =
    WorkDraft(
            workDate = workDate,
            durationMinutes = durationMinutes.toString(),
            memberId = memberId,
            projectId = projectId,
            positionId = positionId,
            description = description.orEmpty(),
        )
        .permitted(selections)

// A2, BR-041: a reference the server no longer offers is left empty for a new permitted choice.
internal fun WorkDraft.permitted(selections: WorkSelectionsResponse): WorkDraft {
    val project = selections.projects.firstOrNull { it.id == projectId }
    return copy(
        memberId = memberId.takeIf { id -> selections.members.any { it.id == id } }.orEmpty(),
        projectId = project?.id.orEmpty(),
        positionId =
            positionId.takeIf { id -> project?.positions?.any { it.id == id } == true }.orEmpty(),
    )
}

// The description is sent exactly as entered; only an empty one is absent.
internal fun WorkDraft.request(expectedRevision: Long?): SaveWorkRequest? {
    val minutes = durationMinutes.trim().toIntOrNull() ?: return null
    return SaveWorkRequest(
        workDate = workDate,
        durationMinutes = minutes,
        memberId = memberId,
        projectId = projectId,
        positionId = positionId,
        description = description.ifEmpty { null },
        expectedRevision = expectedRevision,
    )
}

// A1, A7: only what cannot be sent is checked here; ranges, dates and references are the
// server's decision.
internal fun WorkDraft.unsendableFields(): Map<String, String> = buildMap {
    if (workDate.isBlank()) put("workDate", "INVALID")
    if (durationMinutes.trim().toIntOrNull() == null)
        put("durationMinutes", "WHOLE_MINUTES_REQUIRED")
    if (memberId.isBlank()) put("memberId", "REQUIRED")
    if (projectId.isBlank()) put("projectId", "REQUIRED")
    if (positionId.isBlank()) put("positionId", "REQUIRED")
}
